import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { arrivedByNavigation } from "@/lib/document-entry";

/**
 * Route-change behaviour every template provides (docs/design/ACCESSIBILITY.md § Route change):
 * after an in-app navigation, focus moves to the page's h1, and a polite live region says
 * "{title} loaded". Pass `titleRef` to PageLayout and render `announcement` in a
 * `role="status"` region.
 *
 * A fresh load, reload or restored session keeps focus where the browser puts it (the skip
 * link comes first); any in-app navigation, Back and Forward included, moves it.
 *
 * Keyed on the pathname, not just on mount: React Router reuses the same template instance
 * across parameterised URLs (/people/1 → /people/2). Not on the query string, which T1
 * uses for view state (filters, sort, page), so changing a filter never steals focus.
 *
 * `routeKey` replaces the pathname for pages whose sections are path segments: a T2 tab is
 * `/people/:id/giving`, and switching tabs is not arriving at a new page, so the record
 * template passes the record's own path.
 *
 * Announced once per arrival, not per title change: a record renamed in place (the person-cache
 * update after an edit) changes the heading, not the page. The title is read when the
 * announcement fires, so it is the current one. `pending` holds the announcement while the page
 * is a placeholder (a record still loading) and makes it once the page is ready.
 */
export function usePageArrival(title: string, routeKey?: string, pending = false) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [announcement, setAnnouncement] = useState("");
  const titleText = useRef(title);
  useEffect(() => {
    titleText.current = title;
  }, [title]);

  const { pathname } = useLocation();
  const arrivalKey = routeKey ?? pathname;

  useEffect(() => {
    // preventScroll: after Back, the list's restored scroll position (useListScrollRestoration)
    // must survive the focus move.
    if (arrivedByNavigation()) titleRef.current?.focus({ preventScroll: true });
  }, [arrivalKey]);

  useEffect(() => {
    // Cleared, then set after a beat, so screen readers hear each arrival as a change.
    setAnnouncement("");
    if (pending) return;
    const timer = window.setTimeout(() => setAnnouncement(`${titleText.current} loaded`), 100);
    return () => window.clearTimeout(timer);
  }, [arrivalKey, pending]);

  return { titleRef, announcement };
}
